import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  makeStyles,
  CardContent,
  CardActions
} from "@material-ui/core";
import { Checkbox, FormControlLabel, CardHeader, Button, InputLabel, Link, FormControl, MenuItem, Select } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import { useNavigate, useParams } from "react-router-dom";
import ReCaptchaV2 from "react-google-recaptcha";
import "./RegisterForm.scss";
import IndexService from "../../Services/http/IndexService";
import { setIsActiveSpinner, setuserRol } from "../../Redux/GeneralSlice";
import LoadingOverlay from "react-loading-overlay-ts";
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import PhoneInputField from "../Helper/PhonePrefixForm/PhoneInputField";
import { EstimatedAnnualRevenueEnum } from "../../Models/user";
import { setDefaultAccount } from "../../Redux/AccountSlice";
import { getAffiliateListForRegister } from "../Header/HeaderUtils";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "../../Redux/store";
import OnboardingLayout from "../Layout/OnboardingLayout";

const useStyle = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
  accountName: "",
  sellerId: "",
  mWSauthToken: "",
  recaptcha: "",
  approveTermsConditions: false,
  phoneNumber: "",
  countryCode: "",
  estimatedAnnualRevenue: "",
  freeText: "",
  promotionCode: "",
  affiliate_id: ""
};

const RegisterForm = () => {
  const classes = useStyle();
  const { t, i18n } = useTranslation("registerForm");
  const [isApproveTermMessage, setisApproveTermMessage] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [countryCode, setCountryCode] = useState('');
  const [isApproveTerm, setIsApproveTerm] = useState(false);
  const [errorMessage, seterrorMessage] = useState("");
  const [isShowErrorMessage, setisShowErrorMessage] = useState(false);
  const [existsPromoCode, setExistsPromoCode] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isActive = useSelector((state: any) => state.general.isActiveSpinner);
  const [errorText, setErrorText] = useState('');
  const [contactHubspotId, setContactHubspotId] = useState(null);
  const [finalInitialValues, setFinalInitialValues] = useState(initialValues);
  const { affiliateIdParam } = useParams();
  const affiliatesList = useSelector((state: any) => state.user.affiliates_list);
  const digitsAndEnglishOnly = /^[a-zA-Z0-9]*$/;

  const validationSchema = useMemo(() =>
    Yup.object().shape({
      firstName: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(2, t("errors.passwordLength"))
        .required(t("errors.required")),
      lastName: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(2, t("errors.passwordLength"))
        .required(t("errors.required")),
      email: Yup.string()
        .email(t("errors.invalidEmail"))
        .required(t("errors.required")),
      freeText: Yup.string(),
      password: Yup.string()
        .matches(digitsAndEnglishOnly, t("errors.invalidPassword"))
        .min(8, t("errors.passwordLength"))
        .max(32, t("errors.maxPasswordLength") || "Must contain up to 32 characters!")
        .required(t("errors.required")),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], t("errors.passwordMismatch"))
        .required(t("errors.required")),
      accountName: Yup.string()
        .required(t("errors.required")),
      phoneNumber: Yup.string()
        .required(t("errors.phoneRequired"))
    }), [t]
  );

  useEffect(() => {
    dispatch(getAffiliateListForRegister());
    const params = new URLSearchParams(window.location.search);
    setContactHubspotId(params.get('contactHubspotId'));
    const promo = params.get("promo");
    const affiliateIdFromUrl = params.get("affiliate_id");

    const updatedValues = { ...initialValues };

    if (promo) {
      updatedValues.promotionCode = promo;
      handleCheckPromoCode(promo);
    }

    if (affiliateIdFromUrl) {
      updatedValues.affiliate_id = affiliateIdFromUrl;
    }

    setFinalInitialValues(updatedValues);
  }, [dispatch]);

  const onSubmitRegister = async (values: any) => {
    if (!isApproveTerm)
      setisApproveTermMessage(t("errors.approveTerms"));
    else {
      setisApproveTermMessage("");
      values.countryCode = countryCode;
      values['contactHubspotId'] = contactHubspotId;
      const transformedValues = {
        ...values,
        promotionCode: values.promotionCode ? values.promotionCode : null,
        account: {
          account_name: values.accountName,
          estimated_annual_revenue: values.estimatedAnnualRevenue,
          affiliate_id: values.affiliate_id ? Number(values.affiliate_id) : null
        },
        user: {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          phone: values.phoneNumber,
          password: values.password
        }
      };

      console.log("=== transformedValues ===", transformedValues);
      console.log("affiliate_id in transformedValues:", values.affiliate_id);

      dispatch(setIsActiveSpinner(true));
      let response = await IndexService.register(transformedValues);
      dispatch(setIsActiveSpinner(false));
      let message: string =
        getErrorMessage(
          JSON.parse(JSON.stringify(response))
            ? JSON.parse(JSON.stringify(response)).message
            : null
        ) || "";
      seterrorMessage(message.toString());
      if (message === "success") {
        SessionSorageInstance.setUserDetails(response);
        dispatch(setDefaultAccount(response['account']));
        dispatch(setuserRol(SessionSorageInstance.getUserRole()));
        navigate("/appApproval");
        setisShowErrorMessage(false);
      } else setisShowErrorMessage(true);
    }
  };

  const getErrorMessage = (responseMessage: string) => {
    switch (responseMessage) {
      case null: {
        return t("errors.connection");
      }
      case "success": {
        return "success";
      }
      case "seller_id_exists": {
        return t("errors.sellerIdExists");
      }
      case "email_exists": {
        return t("errors.emailExists");
      }
      default: {
        return responseMessage;
      }
    }
  };

  const onChange = (value: any) => {
    value ? setDisableSubmit(false) : setDisableSubmit(true);
  };

  const onChangeIsApproveTerm = (value: any) => {
    setIsApproveTerm(value.target.checked);
  };

  const returnSiteKey = () => {
    let siteKey: string = process.env.REACT_APP_RECAPTCHA_SITE_KEY as string;
    return siteKey;
  };

  const handleCheckPromoCode = async (value) => {
    if (!value) {
      setExistsPromoCode(true);
      setErrorText('');
      return;
    }
    let response = await IndexService.checkIfExistsPromCode(value);
    if (response) {
      setExistsPromoCode(response['is_exists']);
      setErrorText(response['is_exists'] ? '' : t("errors.invalidPromo"));
    }
  };

  return (
    <OnboardingLayout>
      <LoadingOverlay active={isActive} spinner className="register-align-center">
        <Grid className="register-grid" container justifyContent="center">
          <Grid item>
            <CardHeader title={t("title")} />
            {finalInitialValues &&
              <Formik
                key={i18n.language}
                initialValues={finalInitialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmitRegister}
                initialTouched={{ phoneNumber: true }}
                enableReinitialize={true}
              >
                {({ dirty, isValid, values, handleChange, handleBlur, setFieldValue }) => {
                  return (
                    <Form className="form-width">
                      <CardContent>
                        <Grid item container spacing={1} justifyContent="center">
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("firstName")}*</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              placeholder=""
                              fullWidth
                              required
                              name="firstName"
                              size="small"
                              value={values.firstName}
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("lastName")}*</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              placeholder=""
                              fullWidth
                              required
                              name="lastName"
                              size="small"
                              value={values.lastName}
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("email")}*</InputLabel>
                            <Field
                              label=""
                              placeholder=""
                              fullWidth
                              required
                              name="email"
                              size="small"
                              value={values.email}
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("password")}*</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              fullWidth
                              name="password"
                              placeholder=""
                              value={values.password}
                              type="password"
                              size="small"
                              required
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("confirmPassword")}*</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              fullWidth
                              name="confirmPassword"
                              placeholder=""
                              value={values.confirmPassword}
                              type="password"
                              size="small"
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("phoneNumber")}*</InputLabel>
                            <Field
                              component={PhoneInputField}
                              name="phoneNumber"
                              value={values.phoneNumber}
                              setCountryCode={setCountryCode}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("accountName")}*</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              placeholder=""
                              fullWidth
                              required
                              name="accountName"
                              size="small"
                              value={values.accountName}
                              type="text"
                              component={TextField}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel id="demo-simple-select-label">
                              {t("estimatedRevenue")}*
                            </InputLabel>
                            <FormControl fullWidth>
                              <Select
                                name="estimatedAnnualRevenue"
                                labelId="estimated-annual-revenue-label"
                                id="estimated-annual-revenue"
                                value={values.estimatedAnnualRevenue}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                variant="outlined"
                                size="small"
                              >
                                {Object.keys(EstimatedAnnualRevenueEnum).map((key) =>
                                  <MenuItem value={key} key={key}>{EstimatedAnnualRevenueEnum[key]}</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("affiliateName")}</InputLabel>
                            <FormControl fullWidth>
                              <Select
                                name="affiliate_id"
                                labelId="affiliate-id-label"
                                id="affiliate-id"
                                value={values.affiliate_id}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                variant="outlined"
                                size="small"
                              >
                                <MenuItem value="">None</MenuItem>
                                {affiliatesList && affiliatesList.map((affiliate) =>
                                  <MenuItem value={affiliate.id} key={affiliate.id}>{affiliate.first_name} {affiliate.last_name}</MenuItem>
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("freeText")}</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              placeholder=""
                              fullWidth
                              name="freeText"
                              value={values.freeText}
                              type="text"
                              className="w-100"
                              component="textarea"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={12}>
                            <InputLabel>{t("promotionCode")}</InputLabel>
                            <Field
                              label=""
                              variant="outlined"
                              placeholder=""
                              name="promotionCode"
                              size="small"
                              value={values.promotionCode}
                              type="text"
                              onKeyUp={() => handleCheckPromoCode(values.promotionCode)}
                              component={TextField}
                              error={!existsPromoCode}
                              helperText={errorText}
                            />
                          </Grid>
                          <Grid className="approve-checkbox" item xs={12} sm={6} md={12}>
                            <FormControlLabel
                              control={<Checkbox />}
                              label=""
                              onChange={onChangeIsApproveTerm}
                            />
                            <span className="f-13">
                              {t("terms")}{" "}
                              <a
                                className="blue-color"
                                href={require("../../Assets/Terms_and_Conditions.pdf")}
                                style={{
                                  marginLeft: "3px",
                                  color: "blue",
                                  marginRight: "3px",
                                }}
                                target="_blank"
                              >
                                {t("termsLink")}
                              </a>{" "}
                              and{" "}
                              <a
                                className="blue-color"
                                href={require("../../Assets/privacy_policy.pdf")}
                                style={{
                                  marginLeft: "3px",
                                  color: "blue",
                                  marginRight: "3px",
                                }}
                                target="_blank"
                              >
                                {t("privacyLink")}
                              </a>
                            </span>
                          </Grid>
                          <Grid className="re-captcha">
                            <ReCaptchaV2
                              sitekey={returnSiteKey()}
                              onChange={onChange}
                              hl={i18n.language}
                            />
                            <div style={{ color: "red", marginTop: "10px" }}>
                              {isApproveTermMessage}
                            </div>
                            {isShowErrorMessage && (
                              <div className="error-message">{errorMessage}</div>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                      <CardActions>
                        <Button
                          disabled={!dirty || !isValid || !existsPromoCode}
                          type="submit"
                          className='button-login h-40p'
                        >
                          {t("submit")}
                        </Button>
                      </CardActions>
                      <p className="text-center gray-two-color mt-0 mb-2">
                        {t("alreadyHaveAccount")}
                        <Link className="blue-color pl-3p" underline="hover" href="/">
                          {" "}{t("signin")}
                        </Link>
                      </p>
                    </Form>
                  );
                }}
              </Formik>}
          </Grid>
        </Grid>
      </LoadingOverlay>
    </OnboardingLayout>
  );
};
export default RegisterForm;
